import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Button,
  Flex,
  Tooltip,
  type AlertProps,
} from '@frontend/legacy-components';

import { getReasonText } from 'modules/common/entityActions';
import { useDxfViewerSession } from 'modules/common/viewer/dxfViewer/DxfViewerContext';

import { useDxfMappingSubject } from '../hooks/useDxfMappingSubject';
import { useDxfMappingActions } from '../mappingActions';

/** How far the banner tucks up under the mapping toolbar, as designed. */
export const SPLIT_BANNER_TUCK = '10px';

/**
 * Shown under the mapping toolbar when the mapping draws several flat patterns:
 * Save is blocked by that, and splitting the file into line items is the way on.
 * Visible exactly when `dxfMapping.splitToLineItems` is; its button runs it.
 */
export const DxfSplitToLineItemsBanner = (props: Partial<AlertProps>) => {
  const session = useDxfViewerSession();
  const mapping = useDxfMappingSubject(session);
  const { splitToLineItems } = useDxfMappingActions();

  if (!mapping || !splitToLineItems.getIsVisible(mapping)) {
    return null;
  }

  const isPending = splitToLineItems.getIsPending(mapping);
  const disabledReason = isPending
    ? undefined
    : getReasonText(splitToLineItems.getDisabledReason(mapping));

  return (
    <Alert
      status="info"
      alignItems="center"
      gap={0.75}
      px={1.5}
      pb={1.5}
      // Tucks under the toolbar; the extra top padding keeps the content centred
      // in the part that shows.
      mt={`-${SPLIT_BANNER_TUCK}`}
      pt={`calc(0.75rem + ${SPLIT_BANNER_TUCK})`}
      borderRadius="lg"
      {...props}
    >
      <AlertIcon marginEnd={0} />
      <Flex flex={1} align="center" justify="space-between">
        <Flex gap="0.5rem" whiteSpace="nowrap">
          <AlertTitle marginEnd={0}>Multiple flat patterns</AlertTitle>
          <AlertDescription>
            Split file into line items to continue
          </AlertDescription>
        </Flex>
        <Tooltip
          placement="bottom"
          label={disabledReason}
          isDisabled={disabledReason === undefined}
          shouldWrapChildren={disabledReason !== undefined}
        >
          <Button
            size="md"
            onClick={() => splitToLineItems.execute(mapping)}
            isDisabled={!splitToLineItems.getCanExecute(mapping)}
            isLoading={isPending}
            data-testid="dxf-split-to-line-items"
          >
            {splitToLineItems.label}
          </Button>
        </Tooltip>
      </Flex>
    </Alert>
  );
};
DxfSplitToLineItemsBanner.displayName = 'DxfSplitToLineItemsBanner';
